import { useCallback, useEffect, useState } from 'react';
import type { Models } from 'appwrite';
import type { Employee } from '@zeit/domain';
import { api, auth, read } from './appwrite.ts';
import { adoptServerTime, serverNow } from './clock.ts';
import { unsubscribe } from './push.ts';

export interface Session {
  user: Models.User<Models.Preferences>;
  employee: Employee;
  isAdmin: boolean;
}

type State =
  | { status: 'loading' }
  | { status: 'anonymous' }
  /** Angemeldet, aber ohne Mitarbeiterprofil — ein halb angelegtes Konto. */
  | { status: 'incomplete'; user: Models.User<Models.Preferences> }
  | { status: 'ready'; session: Session };

export function useSession() {
  const [state, setState] = useState<State>({ status: 'loading' });

  const load = useCallback(async () => {
    try {
      const user = await auth.current();
      const [employee, isAdmin] = await Promise.all([
        read.myEmployee(user.$id),
        auth.isAdmin(),
        // Uhrabgleich: scheitert er, laeuft die Anzeige auf der Geraeteuhr
        // weiter — kein Grund, die Anmeldung scheitern zu lassen.
        api.serverTime().then((r) => adoptServerTime(r.now)).catch(() => {}),
      ]);
      if (!employee) {
        setState({ status: 'incomplete', user });
        return;
      }
      setState({ status: 'ready', session: { user, employee, isAdmin } });
    } catch {
      setState({ status: 'anonymous' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const logout = useCallback(async () => {
    try {
      // Erst das Gerät abmelden, solange die Sitzung noch gilt: sonst bekäme
      // auf einem geteilten Gerät die abgemeldete Person weiter Erinnerungen.
      await unsubscribe()
        .then((endpoint) => (endpoint ? api.reminders.unsubscribe(endpoint) : null))
        .catch(() => {});
      await auth.logout();
    } finally {
      setState({ status: 'anonymous' });
    }
  }, []);

  return { state, reload: load, logout };
}

/**
 * Eine Uhr, die im angegebenen Takt neu rendert.
 *
 * Läuft nur, wenn `active` gesetzt ist — steht die Zeit (Tag abgeschlossen,
 * andere Ansicht offen), gibt es keinen Grund, jede Sekunde zu rendern.
 */
export function useNow(active: boolean, intervalMs = 1000): number {
  const [now, setNow] = useState(() => serverNow());

  useEffect(() => {
    if (!active) return;
    setNow(serverNow());
    const id = window.setInterval(() => setNow(serverNow()), intervalMs);
    return () => window.clearInterval(id);
  }, [active, intervalMs]);

  return now;
}

/**
 * Sehr kleiner Router über den Hash-Teil der Adresse.
 *
 * Eine Bibliothek wäre für vier Ansichten mehr Abhängigkeit als Nutzen. Der
 * Hash reicht, weil die Anwendung als statische Datei ausgeliefert wird und
 * es keinen Server gibt, der Pfade auf index.html umschreiben müsste.
 */
export function useRoute(fallback: string): [string, (r: string) => void] {
  const read = () => window.location.hash.replace(/^#\/?/, '') || fallback;
  const [route, setRoute] = useState(read);

  useEffect(() => {
    const onChange = () => setRoute(read());
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallback]);

  const navigate = useCallback((next: string) => {
    window.location.hash = `/${next}`;
  }, []);

  return [route, navigate];
}
